import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';

interface AddMetricModalProps {
  onClose: () => void;
  onAdd: (metric: {
    type: string;
    value: number;
    unit: string;
    date: string;
    notes?: string;
  }) => void;
}

export function AddMetricModal({ onClose, onAdd }: AddMetricModalProps) {
  const { t } = useTranslation();
  const [type, setType] = useState('weight');
  const [value, setValue] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const units: Record<string, string> = {
      weight: 'kg',
      height: 'cm',
      bodyCondition: '/9',
    };

    setSubmitting(true);
    try {
      await onAdd({
        type,
        value: parseFloat(value),
        unit: units[type],
        date,
        notes: notes || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-md w-full border dark:border-white/8">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8">
          <h3
            className="text-xl text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('features.healthRecords.addMetricModal.title')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-white/50 hover:text-gray-600 dark:hover:text-white/70 cursor-pointer"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.healthRecords.addMetricModal.metricType')}
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white disabled:opacity-50"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <option value="weight">
                {t('features.healthRecords.addMetricModal.weight')}
              </option>
              <option value="height">
                {t('features.healthRecords.addMetricModal.heightLength')}
              </option>
              <option value="bodyCondition">
                {t('features.healthRecords.addMetricModal.bodyConditionScore')}
              </option>
            </select>
          </div>

          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {type === 'weight' &&
                t('features.healthRecords.addMetricModal.valueKg')}
              {type === 'height' &&
                t('features.healthRecords.addMetricModal.valueCm')}
              {type === 'bodyCondition' &&
                t('features.healthRecords.addMetricModal.value19')}
            </label>
            <input
              type="number"
              step="0.1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white disabled:opacity-50"
              style={{ fontFamily: 'Inter, sans-serif' }}
              required
            />
          </div>

          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.healthRecords.addMetricModal.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white disabled:opacity-50"
              style={{ fontFamily: 'Inter, sans-serif' }}
              required
            />
          </div>

          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('features.healthRecords.addMetricModal.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent resize-none bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 disabled:opacity-50"
              style={{ fontFamily: 'Inter, sans-serif' }}
              rows={3}
              placeholder={t(
                'features.healthRecords.addMetricModal.notesPlaceholder'
              )}
            />
          </div>

          {/* Blockchain notice */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-3">
            <p
              className="text-xs text-purple-700 dark:text-purple-300"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              🔗 {t('features.healthRecords.addMetricModal.blockchainNotice')}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white disabled:opacity-50 cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('features.healthRecords.addMetricModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('features.healthRecords.addMetricModal.saving')}
                </>
              ) : (
                t('features.healthRecords.addMetricModal.addMetric')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
